import { DeepMap } from 'react-hook-form';
import {
  JobsiteGatewayInput,
  JobsiteUpdateInput,
} from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { Gateways } from 'containers/jobsiteConfiguration/components';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { ensureNonUndefinedFields, typedComponentProps } from 'utils';
import { Jobsite } from './types';

export const getGatewaysSectionInputs = ({
  jobsite,
}: {
  jobsite: Jobsite;
}): TypedFormInputs<EditJobsiteConfigurationFormData['gateways']> => ({
  jobsiteGateways: {
    element: FormInputTypes.CustomInput,
    elementProps: {
      customInput: Gateways,
      // The API closes adding a gateway once a person has published a site
      // layout draft and the site's layout flag is on or can't be read. This
      // list shows only while the page reads that flag as off, so the button
      // closes only when the two disagree, e.g. the flag was turned on after
      // the page read it, or the API can't read it.
      ...typedComponentProps<typeof Gateways>({
        addClosed: Boolean(jobsite?.gatewayAddsClosed),
      }),
    },
    layout: [GridColSpan.SpanFull],
  },
});

export const getGatewaysSectionDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigurationFormData['gateways'] => {
  const { jobsiteGateways } = jobsite ?? {};

  return {
    jobsiteGateways:
      jobsiteGateways?.edges.map(({ node: { id, name } }) => ({ id, name })) ??
      [],
  };
};

type GatewaysSectionUpdateInput = Required<
  Pick<JobsiteUpdateInput, 'jobsiteGateways'>
>;

export const getGatewaysSectionUpdateInput = (
  gateways: EditJobsiteConfigurationFormData['gateways'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['gateways'], true>,
): GatewaysSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    gateways,
    dirtyFields,
  );

  const jobsiteGateways = getUpdateInputValue('jobsiteGateways')?.filter(
    (a): a is JobsiteGatewayInput => !!a.changeType,
  );

  return ensureNonUndefinedFields<GatewaysSectionUpdateInput>({
    jobsiteGateways,
  });
};
